import { useEffect, useMemo, useState } from "react";
import { useJson, nf, control, Chip, countryPath, go, CATEGORY_BY_SLUG, Box } from "./shared";
import { BarList, ChoroplethMap, ColumnChart } from "./Charts";

// Geopolitics › Internet Censorship (#/kb/censorship): which websites and apps are blocked where,
// from OONI's measurements (censorship.js; CC BY-NC-SA 4.0). ?map=confirmed|anomaly picks what the
// map shows. A country's own blocked websites are on its page (CountryCensorship).

const METRICS = [
  { key: "confirmed", label: "Confirmed blocks", unit: "tests", what: "Website tests where the network served a known block page, last 30 days" },
  { key: "anomalyRate", label: "Share of tests with signs of blocking", unit: "% of tests", scale: "linear", what: "Website tests showing an anomaly (blocking, or a network fault), last 30 days, where there were enough tests" },
];
const APP_ORDER = ["whatsapp", "telegram", "signal", "facebook_messenger", "tor"];
const APP_LABEL = { whatsapp: "WhatsApp", telegram: "Telegram", signal: "Signal", facebook_messenger: "Messenger", tor: "Tor" };

const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const dim = (ui, text) => <div style={{ fontSize: ui.fs(12), color: ui.B.textDim, lineHeight: 1.5 }}>{text}</div>;
const pct = (v) => (v === null || v === undefined ? "–" : `${v}%`);

export function OoniCredit({ credit, ui, since, until }) {
  const { B, fs } = ui;
  if (!credit) return null;
  return (
    <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
      Data: <a href={credit.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{credit.source}</a>{since ? `, tests from ${since} to ${until}` : ""},{" "}
      <a href={credit.licenseUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{credit.license}</a>: for non-commercial use with credit, and these figures, made from OONI's, are shared under the same licence.
      OONI's tests are run by volunteers, so a country with few of them is seen only in part; "confirmed" means the network served a known block page, and an anomaly can also be a network fault.
    </div>
  );
}

// An app's state in a country: likely blocked, signs of it, or fine.
function AppCell({ a, ui }) {
  const { B, fs } = ui;
  if (!a) return <span style={{ color: B.textDim }}>–</span>;
  const label = a.confirmed ? "Blocked (confirmed)" : a.likelyBlocked ? `Likely blocked · ${pct(a.anomalyRate)}` : a.anomalyRate === null ? `${nf(a.tests)} tests` : pct(a.anomalyRate);
  return <span style={{ fontSize: fs(12), color: a.likelyBlocked ? B.text : B.textMuted, fontWeight: a.likelyBlocked ? 700 : 400, whiteSpace: "nowrap" }} title={`${nf(a.tests)} tests, ${pct(a.anomalyRate)} with signs of blocking, ${nf(a.confirmed)} confirmed`}>{a.likelyBlocked ? "● " : ""}{label}</span>;
}

function keepMetric(key) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  if (key && key !== METRICS[0].key) q.set("map", key); else q.delete("map");
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

// ─── #/kb/censorship ───

export function KbCensorship({ ui, params = {} }) {
  const { B, fs, HP } = ui;
  const { data, error, loading } = useJson("/api/kb/censorship");
  const [metric, setMetric] = useState(METRICS.some(m => m.key === params.map) ? params.map : METRICS[0].key);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("confirmed");
  useEffect(() => { keepMetric(metric); }, [metric]);
  const cat = CATEGORY_BY_SLUG.censorship;
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data?.countries || []).filter(c => !needle || c.name.toLowerCase().includes(needle));
    const val = (c) => (sort === "name" ? 0 : sort === "apps" ? c.appsBlocked.length : sort === "anomalyRate" ? c.anomalyRate ?? -1 : c[sort]);
    return [...list].sort((a, b) => (sort === "name" ? a.name.localeCompare(b.name) : val(b) - val(a) || a.name.localeCompare(b.name)));
  }, [data, q, sort]);
  const heading = (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{cat.label}</h2>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 16px", maxWidth: "110ch", lineHeight: 1.6 }}>
        Which websites and apps are blocked where, measured by the people living with it: volunteers run OONI Probe on their own connections, and OONI publishes every test.
        This page reads the last 30 days for every country: how often website tests hit a block page, how often they show signs of blocking, and whether WhatsApp, Telegram, Signal, Messenger and Tor get through. Open a country for the websites blocked there.
      </p>
    </>
  );
  if (loading) return <>{heading}<div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <>{heading}<div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  if (!data.builtAt) return <>{heading}{dim(ui, data.error ? `OONI couldn't be reached (${data.error}); trying again within the hour.` : "Loading… OONI's figures arrive a minute or so after the server starts.")}</>;

  const m = METRICS.find(x => x.key === metric);
  const values = Object.fromEntries(data.countries.filter(c => (c[metric] ?? 0) > 0).map(c => [c.iso2, c[metric]]));
  // Measured, zeros included: a share needs enough tests; a count, any test.
  const measured = data.countries.filter(c => (metric === "anomalyRate" ? c.anomalyRate !== null && c.anomalyRate !== undefined : c.tests > 0)).map(c => c.iso2);
  const numeric = Object.fromEntries(data.countries.filter(c => c.numeric).map(c => [c.iso2, c.numeric]));
  const names = Object.fromEntries(data.countries.map(c => [c.iso2, c.name]));
  const pill = (on) => ({ ...HP.navBtn, padding: "5px 11px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const cell = { padding: "8px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), textAlign: "right", fontVariantNumeric: "tabular-nums", color: B.textMuted, verticalAlign: "top" };
  const th = (key, label, left) => (
    <th key={key} style={{ padding: "9px 10px", fontWeight: 700, textAlign: left ? "left" : "right", whiteSpace: "nowrap" }}>
      {key ? <button onClick={() => setSort(key)} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: sort === key ? B.text : "inherit", cursor: "pointer", textTransform: "inherit", letterSpacing: "inherit" }}>{label}{sort === key ? " ↓" : ""}</button> : label}
    </th>
  );
  const blockedApps = data.countries.filter(c => c.appsBlocked.length).sort((a, b) => b.appsBlocked.length - a.appsBlocked.length || a.name.localeCompare(b.name));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>{heading}</div>
      <Box title="Map" ui={ui} right={note(ui, m.what)}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {METRICS.map(x => <button key={x.key} onClick={() => setMetric(x.key)} style={pill(metric === x.key)} aria-pressed={metric === x.key}>{x.label}</button>)}
        </div>
        <ChoroplethMap key={metric} mode="count" values={values} numeric={numeric} names={names} unit={m.unit} scale={m.scale} measured={measured} ui={ui} onSelect={(iso2) => go(countryPath(iso2))} />
        {metric === "anomalyRate" && dim(ui, `Only countries with at least ${data.minTests} website tests. A high share can come from a few networks with faults as well as from blocking.`)}
      </Box>
      {blockedApps.length > 0 && (
        <Box title="Messaging apps and Tor that don't get through" ui={ui} right={note(ui, "confirmed, or signs of blocking in most tests")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {blockedApps.slice(0, 20).map(c => (
              <div key={c.iso2} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12) }}>
                <a href={countryPath(c.iso2)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", minWidth: 140 }}>{c.name}</a>
                <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{c.appsBlocked.map(a => <Chip key={a} ui={ui}>{a}</Chip>)}</span>
              </div>
            ))}
          </div>
        </Box>
      )}
      <Box title={`${nf(data.countries.length)} countries tested`} ui={ui} right={<input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a country" aria-label="Find a country" style={{ ...control(ui), width: 220, textTransform: "none", letterSpacing: 0 }} />}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                {th("name", "Country", true)}{th("tests", "Website tests")}{th("confirmed", "Confirmed blocks")}{th("anomalyRate", "Signs of blocking")}
                {APP_ORDER.map(k => th(null, APP_LABEL[k]))}
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const app = Object.fromEntries(c.apps.map(a => [a.key, a]));
                return (
                  <tr key={c.iso2}>
                    <td style={{ ...cell, textAlign: "left" }}><a href={countryPath(c.iso2)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{c.name}</a></td>
                    <td style={cell}>{nf(c.tests)}</td>
                    <td style={{ ...cell, color: c.confirmed ? B.text : B.textDim, fontWeight: c.confirmed ? 700 : 400 }}>{c.confirmed ? nf(c.confirmed) : "–"}</td>
                    <td style={cell}>{pct(c.anomalyRate)}</td>
                    {APP_ORDER.map(k => <td key={k} style={cell}><AppCell a={app[k]} ui={ui} /></td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Box>
      <OoniCredit credit={data.credit} since={data.since} until={data.until} ui={ui} />
    </div>
  );
}

// ─── On a country's page ───

function DomainList({ items, ui, empty }) {
  const { B, fs } = ui;
  if (!items.length) return dim(ui, empty);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map(d => (
        <div key={d.domain} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: fs(12), minWidth: 0 }}>
          <span style={{ color: B.text, fontWeight: 600, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 auto", minWidth: 0 }}>{d.domain}</span>
          {d.categoryLabel && <span style={{ color: B.textDim, whiteSpace: "nowrap" }}>{d.categoryLabel}</span>}
          <span style={{ color: B.textMuted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }} title={`${nf(d.tests)} tests, ${nf(d.confirmed)} confirmed, ${pct(d.anomalyRate)} with signs of blocking`}>{d.confirmed ? `${nf(d.confirmed)} of ${nf(d.tests)}` : pct(d.anomalyRate)}</span>
        </div>
      ))}
    </div>
  );
}

export function CountryCensorship({ iso2, name, summary, ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson(summary ? `/api/kb/censorship/${iso2}` : null);
  const [all, setAll] = useState(false);
  if (!summary) return null;
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 14, alignItems: "start" };
  const more = <a href={`https://explorer.ooni.org/country/${iso2}`} target="_blank" rel="noreferrer noopener" style={{ fontSize: fs(12), color: B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>OONI Explorer ↗</a>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={grid}>
        <Box title={`Internet censorship in ${name}, last 30 days`} jump="Internet censorship" ui={ui} right={more}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 4 }}>
            {nf(data?.blockedCount ?? summary.confirmed)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>{data ? "websites confirmed blocked" : "website tests confirmed blocked"}</span>
          </div>
          <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 10 }}>{nf(summary.tests)} website tests; {pct(summary.anomalyRate)} showed signs of blocking{data ? `; ${nf(data.anomalousCount)} more websites look blocked without a block page` : ""}.</div>
          {summary.apps.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {APP_ORDER.map(k => summary.apps.find(a => a.key === k)).filter(Boolean).map(a => (
                <div key={a.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: fs(12) }}>
                  <span style={{ color: B.text }}>{a.label}</span><AppCell a={a} ui={ui} />
                </div>
              ))}
            </div>
          )}
        </Box>
        {data?.categories?.length > 0 && (
          <Box title="Blocked by kind of website" listed={false} ui={ui} right={note(ui, "confirmed blocks")}>
            {data.categories.some(c => c.confirmed) ? <BarList rows={data.categories.filter(c => c.confirmed).map(c => ({ label: c.label, value: c.confirmed }))} ui={ui} unit="Tests" maxRows={8} /> : dim(ui, "No block pages seen in any category.")}
          </Box>
        )}
      </div>
      {loading && dim(ui, "Loading the websites tested there from OONI…")}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      {data && (
        <div style={grid}>
          <Box title="Websites confirmed blocked" listed={false} ui={ui} right={note(ui, "tests with a block page, of all tests")}>
            <DomainList items={all ? data.blocked : data.blocked.slice(0, 15)} ui={ui} empty="No website was seen behind a block page." />
            {data.blocked.length > 15 && <button onClick={() => setAll(v => !v)} style={{ ...control(ui), cursor: "pointer", marginTop: 10, padding: "6px 12px" }}>{all ? "Show fewer" : `Show ${data.blocked.length}${data.blockedCount > data.blocked.length ? ` of ${nf(data.blockedCount)}` : ""}`}</button>}
          </Box>
          <Box title="Likely blocked without a block page" listed={false} ui={ui} right={note(ui, "most tests with signs of blocking")}>
            <DomainList items={data.anomalous.slice(0, 15)} ui={ui} empty="None stands out." />
          </Box>
        </div>
      )}
      {data?.days?.length > 0 && (
        <Box title="Confirmed blocks per day, last 90 days" listed={false} ui={ui} right={note(ui, "website tests with a block page")}>
          <ColumnChart points={data.days.map(d => ({ key: d.day, value: d.c }))} ui={ui} unit="confirmed blocks" formatLabel={(k, short) => (short ? k.slice(5) : k)} />
        </Box>
      )}
      {(data || summary) && <OoniCredit credit={data?.credit} since={data?.since || summary.since} until={data?.until || summary.until} ui={ui} />}
    </div>
  );
}
